/*
  MARTIN MARTIN — Custom section "mm-instagram-feed" (update-safe)
  Onglets Publications / Reels, sliders (flèches + barre de progression) et
  lecture des vidéos (quand visibles ou au survol). Aucune dépendance et
  aucun fichier core Sahara modifié.
*/
if (!customElements.get('mm-insta-feed')) {
  class MmInstaFeed extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.autoplay || 'visible';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.canHover = window.matchMedia('(hover: hover)').matches;

      this.tabs = Array.from(this.querySelectorAll('.mm-insta__tab'));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.tab)));

      this.scrollers = Array.from(this.querySelectorAll('.mm-insta__scroller')).map((root) =>
        this.initScroller(root)
      );

      this.initVideos();
      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ---------- Onglets ---------- */

    select(name) {
      this.tabs.forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.tab === name ? 'true' : 'false'));
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== name;
        if (panel.hidden) panel.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.scrollers.forEach((scroller) => scroller.update());
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const panel = event.target.closest('[data-panel]');
      if (panel && panel.hidden) this.select(panel.dataset.panel);
      event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    /* ---------- Sliders ---------- */

    initScroller(root) {
      const track = root.querySelector('[data-track]');
      const nav = root.querySelector('.mm-insta__nav');
      const bar = root.querySelector('[data-progress]');
      let frame;

      const update = () => {
        if (!track || !nav) return;
        const max = track.scrollWidth - track.clientWidth;
        nav.classList.toggle('is-static', max <= 1);
        if (bar && track.scrollWidth > 0) {
          bar.style.width = `${(track.clientWidth / track.scrollWidth) * 100}%`;
          bar.style.transform = `translateX(${(track.scrollLeft / Math.max(track.clientWidth, 1)) * 100}%)`;
        }
      };

      const step = (direction) => {
        const card = track.querySelector('.mm-insta__card');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const amount = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
        const max = track.scrollWidth - track.clientWidth;
        if (direction > 0 && track.scrollLeft >= max - 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (direction < 0 && track.scrollLeft <= 2) {
          track.scrollTo({ left: max, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: direction * amount, behavior: 'smooth' });
        }
      };

      if (track) {
        track.addEventListener(
          'scroll',
          () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(update);
          },
          { passive: true }
        );
        root.querySelector('[data-prev]')?.addEventListener('click', () => step(-1));
        root.querySelector('[data-next]')?.addEventListener('click', () => step(1));

        if ('ResizeObserver' in window) {
          this.resizeObserver = this.resizeObserver || new ResizeObserver(() => this.scrollers?.forEach((s) => s.update()));
          this.resizeObserver.observe(track);
        }
      }

      update();
      return { update };
    }

    /* ---------- Vidéos ---------- */

    initVideos() {
      const videos = Array.from(this.querySelectorAll('video.mm-insta__video'));
      if (!videos.length || this.reduceMotion || this.mode === 'none') return;

      videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
      });

      const play = (video) => {
        if (video.preload === 'none') video.preload = 'auto';
        video.play?.().catch(() => {});
      };

      if (this.mode === 'hover' && this.canHover) {
        videos.forEach((video) => {
          const card = video.closest('.mm-insta__card');
          card?.addEventListener('mouseenter', () => play(video));
          card?.addEventListener('mouseleave', () => video.pause());
        });
        return;
      }

      if (!('IntersectionObserver' in window)) return;
      this.videoObserver = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (entry.isIntersecting) play(entry.target);
            else entry.target.pause();
          }),
        { threshold: 0.6 }
      );
      videos.forEach((video) => this.videoObserver.observe(video));
    }
  }

  customElements.define('mm-insta-feed', MmInstaFeed);
}
